import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Table, TableBody, TableHeader } from '@/components/ui/table';
import {
  PlannedResource,
  PlannedResourceRow,
  useUpdateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import AssignmentCardPlannedResourceEdit from './AssignmentCardPlannedResourceEdit';
import AssignmentCardPlannedResourceView from './AssignmentCardPlannedResourceView';
import AssignmentCardResourceDialog from './AssignmentCardResourceDialog';
import AssignmentCardRosterDialog from './AssignmentCardRosterDialog';
import ResourceSectionAddButtons from './ResourceSectionAddButtons';
import ResourceSectionTableHeader from './ResourceSectionTableHeader';
import {
  emptyPlannedResourceDraft,
  plannedResourceFromDraft,
  type AssignmentCardFormData,
  type PlannedResourceDraft,
} from './assignmentCardFormTypes';
import { ICS204_TABLE } from './assignmentCardStyles';
import {
  ICS204_ADD_DISABLED_AWAITING_REVIEW,
  ICS204_ADD_DISABLED_NO_WALA,
} from './assignmentStatus';
import usePlannedResourceResolver from './usePlannedResourceResolver';

type ResourcesAssignedSectionProps = {
  plannedResources: PlannedResource[] | null;
  actualStatus: string | null;
  walaId: string | null;
  periodNumber?: number;
  canEdit?: boolean;
  disabledReason?: string | null;
};

type EditingState =
  | { kind: 'new'; id: string }
  | { kind: 'existing'; id: string }
  | null;

const draftFromRow = (res: PlannedResourceRow): PlannedResourceDraft => ({
  resourceName: res.name,
  leader: res.leader ?? '',
  numberOfPersons: res.numberOfPersons ?? 0,
  contactInfo: res.contactInfo ?? '',
  reportingInfo: res.reportingInfo ?? '',
  has204a: !!res.has204a,
  isDocumentationEntry: !!res.isDocumentationEntry,
  resourceBranch: res.resourceBranch ?? '',
  resourceDivision: res.resourceDivision ?? '',
  resourceGroup: res.resourceGroup ?? '',
  resourceStrikeTeam: res.resourceStrikeTeam ?? '',
  resourceTaskForce: res.resourceTaskForce ?? '',
  resourceAssignmentLocation: res.resourceAssignmentLocation ?? '',
  resourceSpecialInstructions: res.resourceSpecialInstructions ?? '',
  resourceSafetyPlanLocation: res.resourceSafetyPlanLocation ?? '',
});

/**
 * Box 5 on the tip path: persistent Add + per-row Check/Cancel/Trash (immediate
 * API). No section-level edit chrome.
 */
const ResourcesAssignedSection = ({
  plannedResources,
  actualStatus,
  walaId,
  periodNumber,
  canEdit = true,
  disabledReason = null,
}: ResourcesAssignedSectionProps) => {
  const { mutateAsync: updateWala } = useUpdateWorkAssignmentListAttachment();
  const plannedResourceData = usePlannedResourceResolver(
    plannedResources,
    walaId
  );
  const [editing, setEditing] = useState<EditingState>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showRosterModal, setShowRosterModal] = useState(false);

  const statusBlocked =
    actualStatus === 'awaiting_review' ||
    actualStatus === null ||
    walaId === null;
  const addDisabled = !canEdit || statusBlocked || isSaving;
  const addDisabledReason =
    disabledReason ??
    (statusBlocked
      ? actualStatus === 'awaiting_review'
        ? ICS204_ADD_DISABLED_AWAITING_REVIEW
        : walaId === null
          ? ICS204_ADD_DISABLED_NO_WALA
          : null
      : null);

  const editingExistingId = editing?.kind === 'existing' ? editing.id : null;

  const startEdit = (res: PlannedResourceRow) => {
    if (!canEdit || isSaving) return;
    setEditing({ kind: 'existing', id: res.id });
  };

  const startDocumentationEntry = () => {
    if (addDisabled) return;
    setEditing({ kind: 'new', id: crypto.randomUUID() });
  };

  const cancelEdit = () => {
    setEditing(null);
  };

  const persistList = async (next: PlannedResource[]) => {
    if (!walaId) return;
    await updateWala({
      walaId,
      plannedResources: next,
      touch: true,
    });
  };

  const confirmEdit = async (draft: PlannedResourceDraft, id: string) => {
    if (!walaId) return;
    setIsSaving(true);
    try {
      const current = plannedResources ?? [];
      const nextResource = plannedResourceFromDraft(id, draft);
      const next =
        editing?.kind === 'new'
          ? [nextResource, ...current]
          : current.map((r) => (r.id === id ? nextResource : r));
      await persistList(next);
      setEditing(null);
    } catch {
      // Mutation hooks toast; keep the row open for retry.
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRow = async (id: string) => {
    if (!canEdit || isSaving || !walaId) return;
    setIsSaving(true);
    try {
      const next = (plannedResources ?? []).filter((r) => r.id !== id);
      await persistList(next);
      if (editing && editing.id === id) setEditing(null);
    } catch {
      // Mutation hooks toast.
    } finally {
      setIsSaving(false);
    }
  };

  const showTable = plannedResourceData.length > 0 || editing?.kind === 'new';

  return (
    <>
      <ResourceSectionAddButtons
        disabled={addDisabled}
        disabledReason={addDisabled ? addDisabledReason : null}
        onAddResource={() => {
          if (addDisabled) return;
          setShowResourceModal(true);
        }}
        onAddFromRoster={() => {
          if (addDisabled) return;
          setShowRosterModal(true);
        }}
        onAddDocumentationEntry={startDocumentationEntry}
      />
      {showTable ? (
        <div
          data-testid="ics204-planned-resources"
          className="tw:min-w-0 tw:max-w-full tw:overflow-x-auto tw:overscroll-x-contain"
        >
          <Table className={ICS204_TABLE}>
            <TableHeader>
              <ResourceSectionTableHeader />
            </TableHeader>
            <TableBody>
              {editing?.kind === 'new' ? (
                <ResourceEditRowHost
                  resourceId={editing.id}
                  initialDraft={emptyPlannedResourceDraft('', {
                    isDocumentationEntry: true,
                  })}
                  periodNumber={periodNumber}
                  walaId={walaId}
                  isSaving={isSaving}
                  onConfirm={(draft) => void confirmEdit(draft, editing.id)}
                  onCancel={cancelEdit}
                />
              ) : null}
              {plannedResourceData.map((res, idx) =>
                editingExistingId === res.id ? (
                  <ResourceEditRowHost
                    key={`${res.id}-${idx}`}
                    resourceId={res.id}
                    initialDraft={draftFromRow(res)}
                    periodNumber={periodNumber}
                    walaId={walaId}
                    isSaving={isSaving}
                    onConfirm={(draft) => void confirmEdit(draft, res.id)}
                    onCancel={cancelEdit}
                  />
                ) : (
                  <AssignmentCardPlannedResourceView
                    key={`${res.id}-${idx}`}
                    plannedResource={res}
                    periodNumber={periodNumber}
                    canEdit={canEdit}
                    disabled={isSaving}
                    onStartEdit={() => startEdit(res)}
                    onDelete={() => void deleteRow(res.id)}
                  />
                )
              )}
            </TableBody>
          </Table>
        </div>
      ) : null}
      {showResourceModal && walaId && !addDisabled && (
        <AssignmentCardResourceDialog
          walaId={walaId}
          onClose={() => setShowResourceModal(false)}
          plannedResources={plannedResources}
        />
      )}
      {showRosterModal && walaId && !addDisabled && (
        <AssignmentCardRosterDialog
          walaId={walaId}
          onClose={() => setShowRosterModal(false)}
          plannedResources={plannedResources}
        />
      )}
    </>
  );
};

const ResourceEditRowHost = ({
  resourceId,
  initialDraft,
  periodNumber,
  walaId,
  isSaving,
  onConfirm,
  onCancel,
}: {
  resourceId: string;
  initialDraft: PlannedResourceDraft;
  periodNumber?: number;
  walaId?: string | null;
  isSaving: boolean;
  onConfirm: (draft: PlannedResourceDraft) => void;
  onCancel: () => void;
}) => {
  const form = useForm<Pick<AssignmentCardFormData, 'resource'>>({
    defaultValues: {
      resource: { [resourceId]: initialDraft },
    },
  });

  useEffect(() => {
    form.reset({ resource: { [resourceId]: initialDraft } });
    // Only remount/reset when switching rows; draft is seeded once per edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [resourceId]);

  return (
    <FormProvider {...form}>
      <AssignmentCardPlannedResourceEdit
        resourceId={resourceId}
        periodNumber={periodNumber}
        walaId={walaId}
        showRowActions
        isSaving={isSaving}
        onConfirm={() => {
          const draft = form.getValues(`resource.${resourceId}`);
          if (draft) onConfirm(draft);
        }}
        onCancel={onCancel}
      />
    </FormProvider>
  );
};

export default ResourcesAssignedSection;
